/**
 * Cross-context UUID v4 generator.
 *
 * `crypto.randomUUID` is only defined in *secure contexts* (HTTPS or localhost).
 * When the admin is served over plain `http://<ip>:5173` for dev, the function
 * is undefined and code crashes. Fall back to a v4 built from `getRandomValues`
 * (which is available in all browsers, secure context or not).
 */
export function randomUUID(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID();
  }
  if (c && typeof c.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    c.getRandomValues(bytes);
    // RFC 4122 v4: bits for version and variant
    bytes[6] = (bytes[6]! & 0x0f) | 0x40;
    bytes[8] = (bytes[8]! & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  // Last-ditch fallback — not cryptographically strong; only hit if WebCrypto is absent entirely.
  const r = (n: number) => Math.floor(Math.random() * n);
  const rand = () => r(0x100000000).toString(16).padStart(8, '0');
  return `${rand().slice(0, 8)}-${rand().slice(0, 4)}-4${rand().slice(1, 4)}-${(0x8 + r(4)).toString(16)}${rand().slice(1, 4)}-${rand()}${rand().slice(0, 4)}`;
}
